import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getApiUrl } from '@/lib/api-config';

interface RadarAddressComponents {
  number?: string;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface AddressSuggestion {
  formattedAddress: string;
  addressLabel?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  address?: RadarAddressComponents;
}

export interface AddressSelection {
  addressLine1: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  fullAddress: string;
  latitude?: number;
  longitude?: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectSuggestion: (selection: AddressSelection) => void;
  placeholder?: string;
  minQueryLength?: number;
  className?: string;
  inputClassName?: string;
  initialSelected?: boolean;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelectSuggestion,
  placeholder = 'Search address',
  minQueryLength = 3,
  className,
  inputClassName,
  initialSelected = false
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [hasSelected, setHasSelected] = useState(initialSelected);
  const userHasInteractedRef = useRef(false);
  const previousInitialSelectedRef = useRef(initialSelected);

  useEffect(() => {
    // Only update hasSelected from initialSelected if it changed, not on every render
    if (previousInitialSelectedRef.current !== initialSelected) {
      setHasSelected(initialSelected);
      previousInitialSelectedRef.current = initialSelected;
      // Reset user interaction flag when initialSelected changes
      if (initialSelected) {
        userHasInteractedRef.current = false;
      }
    }
  }, [initialSelected]);

  useEffect(() => {
    if (!value) {
      setHasSelected(false);
    } else if (initialSelected && value.trim().length >= minQueryLength && !userHasInteractedRef.current) {
      // If we have initialSelected and a value that could trigger suggestions,
      // and the user hasn't manually interacted, keep hasSelected as true
      // This prevents suggestions from showing when editing an existing record
      setHasSelected(true);
    }
  }, [value, initialSelected, minQueryLength]);

  useEffect(() => {
    if (hasSelected) {
      setSuggestions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!value || value.trim().length < minQueryLength) {
      setSuggestions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setIsLoading(true);
        setError(null);

        const params = new URLSearchParams({
          query: value,
          size: '5'
        });

        const response = await fetch(`${getApiUrl('api/address-autocomplete')}?${params.toString()}`, {
          method: 'GET',
          signal: controller.signal
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
          throw new Error(errorData.error || `Request failed (${response.status})`);
        }

        const data = await response.json();
        if (!isCancelled) {
          setSuggestions(Array.isArray(data?.addresses) ? data.addresses : []);
        }
      } catch (err) {
        if (!isCancelled && err instanceof Error && err.name !== 'AbortError') {
          setError(err.message);
          setSuggestions([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [value, minQueryLength, hasSelected]);

  const extractComponents = (suggestion: AddressSuggestion): AddressSelection => {
    const address = suggestion.address ?? {};

    const number = address.number ?? '';
    const street = address.street ?? '';
    const unit = address.unit ?? '';

    const line1Source = [number, street].filter(Boolean).join(' ');
    const line1Fallback = suggestion.addressLabel?.split(',')[0]?.trim() ?? suggestion.formattedAddress.split(',')[0]?.trim();
    const rawLine = line1Source || line1Fallback || suggestion.formattedAddress;
    const line1 = unit ? `${rawLine} ${unit}`.trim() : rawLine;

    const city = address.city ?? suggestion.city;
    const state = address.state ?? suggestion.state;
    const postalCode = address.postalCode ?? suggestion.postalCode;
    const country = address.country ?? suggestion.country;

    return {
      addressLine1: line1,
      city: city || undefined,
      state: state || undefined,
      postalCode: postalCode || undefined,
      country: country || undefined,
      fullAddress: suggestion.formattedAddress,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
    };
  };

  const handleSelect = (suggestion: AddressSuggestion) => {
    if (!suggestion?.formattedAddress) {
      return;
    }

    const selection = extractComponents(suggestion);
    onChange(selection.addressLine1);
    onSelectSuggestion(selection);
    setHasSelected(true);
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      setSuggestions([]);
      setActiveIndex(-1);
    }
  };

  return (
    <div className={`address-autocomplete relative ${className ?? ''}`}>
      <input
        type="text"
        value={value}
        onChange={(event) => {
          userHasInteractedRef.current = true;
          setHasSelected(false);
          onChange(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Suggestion clicks use onMouseDown+preventDefault below, so they
          // never trigger this blur -- this only fires when focus genuinely
          // leaves the field (e.g. clicking elsewhere on the page) without a
          // suggestion being picked, which should just close the list rather
          // than leave it stuck open over whatever the user typed.
          setSuggestions([]);
          setActiveIndex(-1);
        }}
        placeholder={placeholder}
        className={`flex h-10 w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 font-form-input generic-form-field ${suggestions.length > 0 && !hasSelected ? 'ring-1 ring-ring border-ring' : 'border-gray-300'} ${inputClassName ?? ''}`}
        autoComplete="off"
      />

      {isLoading && value.length >= minQueryLength && (
        <div className="mt-1 text-xs text-gray-500">Searching…</div>
      )}

      {error && (
        <div className="mt-1 text-xs text-destructive">{error}</div>
      )}

      {suggestions.length > 0 && !hasSelected && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto py-1">
          {suggestions.map((suggestion, index) => {
            const components = extractComponents(suggestion);
            const secondaryText = components.fullAddress
              .replace(components.addressLine1, '')
              .replace(/^,\s*/, '');

            return (
              <li
                key={suggestion.placeId || `${suggestion.formattedAddress}-${index}`}
                className={`px-3 py-2 cursor-pointer ${index === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(suggestion);
                }}
              >
                <span className="block text-sm text-gray-900">
                  {suggestion.addressLabel || suggestion.formattedAddress.split(',')[0]}
                </span>
                {secondaryText && (
                  <span className="block text-xs text-gray-500">
                    {secondaryText}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;

